import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { orderService } from '../services/api';
import { plisioService } from '../services/plisio';
import Breadcrumbs from '../components/Breadcrumbs';
import Spinner from '../components/Spinner';

const CheckoutPage = () => {
  const navigate = useNavigate();
  const [formData, setFormData] = useState({
    customerName: '',
    customerEmail: '',
    region: '',
    phone: '',
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Check for payment error from URL
  React.useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get('error') === 'payment_failed') {
      setError('Payment was cancelled or failed. Please try again.');
    }
  }, []);

  const cart = JSON.parse(localStorage.getItem('cart') || '[]');

  const regions = [
    'Afghanistan', 'Albania', 'Algeria', 'Andorra', 'Angola', 'Argentina', 'Armenia', 'Australia', 'Austria', 'Azerbaijan',
    'Bahamas', 'Bahrain', 'Bangladesh', 'Barbados', 'Belarus', 'Belgium', 'Belize', 'Benin', 'Bhutan', 'Bolivia', 'Bosnia and Herzegovina', 'Botswana', 'Brazil', 'Brunei', 'Bulgaria', 'Burkina Faso', 'Burundi',
    'Cambodia', 'Cameroon', 'Canada', 'Cape Verde', 'Central African Republic', 'Chad', 'Chile', 'China', 'Colombia', 'Comoros', 'Congo', 'Costa Rica', 'Croatia', 'Cuba', 'Cyprus', 'Czech Republic',
    'Denmark', 'Djibouti', 'Dominica', 'Dominican Republic',
    'Ecuador', 'Egypt', 'El Salvador', 'Equatorial Guinea', 'Eritrea', 'Estonia', 'Eswatini', 'Ethiopia',
    'Fiji', 'Finland', 'France',
    'Gabon', 'Gambia', 'Georgia', 'Germany', 'Ghana', 'Greece', 'Grenada', 'Guatemala', 'Guinea', 'Guinea-Bissau', 'Guyana',
    'Haiti', 'Honduras', 'Hungary',
    'Iceland', 'India', 'Indonesia', 'Iran', 'Iraq', 'Ireland', 'Israel', 'Italy', 'Ivory Coast',
    'Jamaica', 'Japan', 'Jordan',
    'Kazakhstan', 'Kenya', 'Kiribati', 'Kosovo', 'Kuwait', 'Kyrgyzstan',
    'Laos', 'Latvia', 'Lebanon', 'Lesotho', 'Liberia', 'Libya', 'Liechtenstein', 'Lithuania', 'Luxembourg',
    'Madagascar', 'Malawi', 'Malaysia', 'Maldives', 'Mali', 'Malta', 'Marshall Islands', 'Mauritania', 'Mauritius', 'Mexico', 'Micronesia', 'Moldova', 'Monaco', 'Mongolia', 'Montenegro', 'Morocco', 'Mozambique', 'Myanmar',
    'Namibia', 'Nauru', 'Nepal', 'Netherlands', 'New Zealand', 'Nicaragua', 'Niger', 'Nigeria', 'North Korea', 'North Macedonia', 'Norway',
    'Oman',
    'Pakistan', 'Palau', 'Palestine', 'Panama', 'Papua New Guinea', 'Paraguay', 'Peru', 'Philippines', 'Poland', 'Portugal',
    'Qatar',
    'Romania', 'Russia', 'Rwanda',
    'Saint Kitts and Nevis', 'Saint Lucia', 'Saint Vincent and the Grenadines', 'Samoa', 'San Marino', 'Sao Tome and Principe', 'Saudi Arabia', 'Senegal', 'Serbia', 'Seychelles', 'Sierra Leone', 'Singapore', 'Slovakia', 'Slovenia', 'Solomon Islands', 'Somalia', 'South Africa', 'South Korea', 'South Sudan', 'Spain', 'Sri Lanka', 'Sudan', 'Suriname', 'Sweden', 'Switzerland', 'Syria',
    'Taiwan', 'Tajikistan', 'Tanzania', 'Thailand', 'Timor-Leste', 'Togo', 'Tonga', 'Trinidad and Tobago', 'Tunisia', 'Turkey', 'Turkmenistan', 'Tuvalu',
    'Uganda', 'Ukraine', 'United Arab Emirates', 'United Kingdom', 'United States', 'Uruguay', 'Uzbekistan',
    'Vanuatu', 'Vatican City', 'Venezuela', 'Vietnam',
    'Yemen',
    'Zambia', 'Zimbabwe'
  ];

  const getCustomerIP = async () => {
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 3000);
      
      const response = await fetch('https://api.ipify.org?format=json', {
        signal: controller.signal
      });
      clearTimeout(timeoutId);
      
      const data = await response.json();
      return data.ip;
    } catch {
      return 'unknown';
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      const total = cart.reduce((sum, item) => {
        const price = item.selectedPrice || item.price;
        return sum + (price * item.quantity);
      }, 0);

      // Get customer IP
      const customerIP = await getCustomerIP();

      const orderData = {
        customerName: formData.customerName,
        customerEmail: formData.customerEmail,
        region: formData.region,
        phone: formData.phone,
        totalAmount: total,
        customer_ip: customerIP,
        items: cart.map(item => ({
          productId: item.productId,
          name: item.name,
          quantity: item.quantity,
          platform: item.selectedPlatform,
          price: item.selectedPrice || item.price,
          imageUrl: item.imageUrl
        }))
      };

      setLoading(true);
      const response = await orderService.create(orderData);
      
      // Create Plisio payment invoice
      const paymentData = {
        order_number: response.data.order_number,
        totalAmount: total,
        customerEmail: formData.customerEmail
      };
      
      const plisioResponse = await plisioService.createInvoice(paymentData);
      
      // Clear cart
      localStorage.removeItem('cart');
      
      // Navigate to payment page with iframe
      navigate(`/payment?url=${encodeURIComponent(plisioResponse.invoiceUrl)}&orderId=${response.data.order_number}`);
    } catch (err) {
      setError(err.message || 'Failed to create order');
      setLoading(false);
    }
  };

  const total = cart.reduce((sum, item) => {
    const price = item.selectedPrice || item.price;
    return sum + (price * item.quantity);
  }, 0);

  return (
    <div className="checkout-page">
      <Breadcrumbs items={[
        { label: 'Home', link: '/' },
        { label: 'Cart', link: '/cart' },
        { label: 'Checkout' }
      ]} />
      <h1>Checkout</h1>

      <div className="order-summary">
        <h2>Order Summary</h2>
        {cart.map(item => {
          const itemPrice = item.selectedPrice || item.price;
          return (
          <div key={item.cartKey || item.productId} className="order-item">
            <span>
              {item.name} {item.selectedPlatform && `(${item.selectedPlatform.toUpperCase()})`} x {item.quantity}
            </span>
            <span>${(itemPrice * item.quantity).toFixed(2)}</span>
          </div>
        )})}
        <div className="total">
          <strong>Total: ${total.toFixed(2)}</strong>
        </div>
      </div>

      <form onSubmit={handleSubmit} className="checkout-form">
        <h2>Customer Information</h2>
        
        <input
          type="text"
          placeholder="Full Name"
          value={formData.customerName}
          onChange={(e) => setFormData({...formData, customerName: e.target.value})}
          required
        />

        <input
          type="email"
          placeholder="Email Address"
          value={formData.customerEmail}
          onChange={(e) => setFormData({...formData, customerEmail: e.target.value})}
          required
        />

        <select
          value={formData.region}
          onChange={(e) => setFormData({...formData, region: e.target.value})}
          required
          className="region-select"
        >
          <option value="">Select Country/Region *</option>
          {regions.map(region => (
            <option key={region} value={region}>{region}</option>
          ))}
        </select>

        <input
          type="tel"
          placeholder="Phone Number (Optional)"
          value={formData.phone}
          onChange={(e) => setFormData({...formData, phone: e.target.value})}
        />

        {error && <div className="error">{error}</div>}

        <button type="submit" disabled={loading} className="submit-btn" style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '10px' }}>
          {loading ? (
            <><Spinner size="20px" /> Redirecting to Payment Gateway...</>
          ) : (
            '💳 Proceed to Payment'
          )}
        </button>
      </form>
    </div>
  );
};

export default CheckoutPage;
